import React from 'react';
import { Download } from 'lucide-react';
import DeploymentFrequencyDoraCard from '../../../components/dora/DeploymentFrequencyDoraCard';
import LeadTimeDoraCard from '../../../components/dora/LeadTimeDoraCard';
import ChurnRatioCard from '../../../components/dora/ChurnRatioCard';
import CodeCompositionCard from '../../../components/dora/CodeCompositionCard';
import CodeCompositionOverTimeCard from '../../../components/dora/CodeCompositionOverTimeCard';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';

export default function RepositoryMetricsTab({
  deploymentLoading,
  deploymentDora,
  deploymentDoraMeta,
  deploymentWindowDays,
  deploymentPeriod,
  doraChartType,
  setDeploymentPeriod,
  setDoraChartType,
  leadTimeLoading,
  leadTimeDora,
  leadTimeDoraMeta,
  leadTimePeriod,
  leadTimeChartType,
  setLeadTimePeriod,
  setLeadTimeChartType,
  churnLoading,
  churnData,
  churnWindowDays,
  churnPeriod,
  churnChartType,
  setChurnPeriod,
  setChurnChartType,
  onDownloadDoraLabelsTemplate,
  metricsFetchScope,
  doraLabelsFilePresent,
}: any) {
  return (
    <div
      role="tabpanel"
      id="repo-tabpanel-metrics"
      aria-labelledby="repo-tab-metrics"
      className="dashboard-org-tight"
    >
      <FeatureGate flag="ui.repositoryDetail.cycleTimeSection">
        <div className="section-block-mdl">
          <CodeCompositionOverTimeCard className="!mt-0" fetchScope={metricsFetchScope} />
        </div>
      </FeatureGate>

      <FeatureGate flag="ui.repositoryDetail.doraSection">
        <div className="section-block-mdl space-y-4">
          <div className="card-grid-2-mdl">
            <DeploymentFrequencyDoraCard
              loading={deploymentLoading}
              noData={!deploymentDora}
              dates={deploymentDoraMeta?.mergeDeploymentsByDay?.dates}
              counts={deploymentDoraMeta?.mergeDeploymentsByDay?.counts}
              windowDays={deploymentWindowDays}
              period={deploymentPeriod}
              chartType={doraChartType}
              onPeriodChange={setDeploymentPeriod}
              onChartTypeChange={setDoraChartType}
              emptyMessage="No merges to default branch in this window. Sync the repo to load commits."
            />
            <LeadTimeDoraCard
              loading={leadTimeLoading}
              noData={!leadTimeDora}
              dates={leadTimeDoraMeta?.leadTimeByDeployment?.dates}
              leadTimeMinutes={leadTimeDoraMeta?.leadTimeByDeployment?.leadTimeMinutes}
              meanLeadTimeMinutes={leadTimeDoraMeta?.meanLeadTimeMinutes}
              period={leadTimePeriod}
              chartType={leadTimeChartType}
              onPeriodChange={setLeadTimePeriod}
              onChartTypeChange={setLeadTimeChartType}
              emptyMessage="No merges to default branch in this window. Sync the repo to load commits."
            />
          </div>
          <div className="card-grid-2-mdl items-stretch">
            <ChurnRatioCard
              className="!mt-0 h-full"
              loading={churnLoading}
              noData={
                !churnData ||
                !churnData.dates?.length ||
                (churnData.ratios?.length > 0 && churnData.ratios.every((r) => r === 0))
              }
              dates={churnData?.dates}
              ratios={churnData?.ratios}
              windowDays={churnWindowDays}
              period={churnPeriod}
              chartType={churnChartType}
              onPeriodChange={setChurnPeriod}
              onChartTypeChange={setChurnChartType}
              emptyMessage="No commit data in this window. Sync the repo to load commits."
            />
            <FeatureGate flag="ui.repositoryDetail.cycleTimeSection">
              <CodeCompositionCard className="!mt-0 h-full" fetchScope={metricsFetchScope} />
            </FeatureGate>
          </div>
        </div>
      </FeatureGate>

      <FeatureGate flag="ui.repositoryDetail.doraLabelsSection">
        <div className="section-block-mdl">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onDownloadDoraLabelsTemplate}
              className="inline-flex items-center gap-2 rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm font-medium text-slate-200 hover:bg-dark-base/80"
            >
              <Download className="w-4 h-4" aria-hidden />
              Download <code className="text-xs">dora-labels.json</code> template
            </button>
            {doraLabelsFilePresent ? (
              <span className="text-sm text-vivid-green-cyan">
                Convention file detected on deployment branch (last sync).
              </span>
            ) : (
              <span className="text-sm text-slate-400">
                Add <code className="text-xs">.vantagepoint/dora-labels.json</code> on your
                deployment branch and sync.
              </span>
            )}
          </div>
        </div>
      </FeatureGate>
    </div>
  );
}
